import type { UserTypes } from "db/types";
import { apiRequest } from "../http";

const SESSION_COOKIE_NAME = "notebook_session";

/**
 * Get OAuth login URL with redirect
 *
 * @param returnTo - Path to redirect to after login (defaults to current path)
 * @returns Full URL to Google OAuth endpoint with returnTo parameter
 *
 * @example
 * const loginUrl = getLoginUrl('/notes');
 * window.location.href = loginUrl;
 */
export function getLoginUrl(returnTo?: string): string {
  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : process.env.PUBLIC_URL || "http://localhost:3030";

  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const returnUrl = returnTo
    ? `${baseUrl}${returnTo}`
    : typeof window !== "undefined"
      ? window.location.href
      : baseUrl;

  return `${apiUrl}/auth/google?returnTo=${encodeURIComponent(returnUrl)}`;
}

/**
 * Logout user (clears session on server and client, then reloads)
 *
 * @example
 * await logout();
 */
export async function logout(): Promise<void> {
  try {
    // Call logout API endpoint
    await apiRequest("/auth/logout", { method: "POST" });
  } catch (error) {
    console.error("Logout API call failed:", error);
  } finally {
    if (typeof window !== "undefined") {
      // Ensure session cookies are cleared (primary + legacy)
      document.cookie = `${SESSION_COOKIE_NAME}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
      document.cookie =
        "__session=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";

      // Refresh the app so SSR picks up logged-out state
      window.location.assign("/");
    }
  }
}

/**
 * Redirect to login page
 *
 * @param returnTo - Path to redirect to after login
 *
 * @example
 * redirectToLogin('/notes/123');
 */
export function redirectToLogin(returnTo?: string): void {
  if (typeof window === "undefined") return;

  const loginUrl = getLoginUrl(returnTo || window.location.pathname);
  window.location.href = loginUrl;
}

/**
 * Get current authenticated user
 *
 * @example
 * const user = await getCurrentUser();
 */
export const getCurrentUser = () =>
  apiRequest<UserTypes.SerializedUser>("/auth/me");

/**
 * Check if user is authenticated (client-side only)
 *
 * @returns true if an auth session cookie exists
 *
 * @example
 * if (isAuthenticated()) {
 *   // Show authenticated UI
 * }
 */
export function isAuthenticated(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((cookie) => {
    const [name, value] = cookie.trim().split("=");
    return name === SESSION_COOKIE_NAME && Boolean(value);
  });
}
